<script setup>
/*==================================================================*/
/*================== VUE IMPORTS ===================================*/
/*==================================================================*/

import { defineProps, defineExpose, ref, computed } from 'vue';

/*==================================================================*/
/*===================== IMPORTS UTILS ==============================*/
/*==================================================================*/

import UtilID from '@/utils/UtilID.js';
import UtilObject from '@/utils/UtilObject';
import UtilDateTime from '@/utils/UtilDateTime';
import UtilMoney from '@/utils/UtilMoney';
import UtilURL from '@/utils/UtilURL';
import UtilFile from '@/utils/UtilFile';

/*==================================================================*/
/*================ IMPORTS COMPONENTES =============================*/
/*==================================================================*/

import ModalFullscreen from '@/components/v2/modais/ModalFullscreen/ModalFullscreen.vue';
import BotaoTextoBase from '@/components/v2/base/BotaoTextoBase/BotaoTextoBase.vue';
import ConteudoModal from './ConteudoModal.vue';

/*==================================================================*/
/*================ IMPORTS SERVICES ================================*/
/*==================================================================*/

import OrdemDePagamento from '@/api/v2/services/OrdemDePagamentoService';

/*==================================================================*/
/*================== PROPS =========================================*/
/*==================================================================*/

const props = defineProps({
    disabled: {
        type: Boolean,
        default: false
    },
    ordemDePagamentoRequisitantes: {
        type: Array,
        default: () => []
    },
    ordemDePagamentoCredorColaboradores: {
        type: Array,
        default: () => []
    },
    ordemDePagamentoCredorFornecedores: {
        type: Array,
        default: () => []
    },
    ordemDePagamentoDespesas: {
        type: Array,
        default: () => []
    },
    ordemDePagamentoJobs: {
        type: Array,
        default: () => []
    },
    onClose: {
        type: Function,
        default: null
    }
});

/*==================================================================*/
/*================== MODAL FULLSCREEN REF ==========================*/
/*==================================================================*/

const modalFullscreenRef = ref();

/*==================================================================*/
/*================== DADO ==========================================*/
/*==================================================================*/

const dado = ref({});

/*==================================================================*/
/*=================== COMPONENTS REFS ==============================*/
/*==================================================================*/

const conteudoModalRef = ref(null);

/*==================================================================*/
/*================== PAGAMENTO =====================================*/
/*==================================================================*/

const pagamento = computed(() => {

    return dado?.value?.parcelas?.[0] || {};

});

/*==================================================================*/
/*================== DISABLED EDITAR ORDEM DE PAGAMENTO =============*/
/*==================================================================*/

const disabledEditarOrdemDePagamento = computed(() => {

    // Desabilita o botão se o modal estiver desativado.
    if(props.disabled){
        return true;
    }

    // Desabilita se algum cartão não for válido
    if (conteudoModalRef.value?.isValid === false) {
        return true;
    }

    return false;

});

/*==================================================================*/
/*================== DISABLED ENVIAR PARA APROVAÇÃO ================*/
/*==================================================================*/

const disabledEnviarParaAprovacao = computed(() => {

    if(dado?.value?.alterado === false){
        return false;
    }

    return true;

});

/*==================================================================*/
/*================== MOSTRA ENVIAR PARA APROVAÇÃO ==================*/
/*==================================================================*/

const mostraEnviarParaAprovacao = computed(() => {

    if(disabledEnviarParaAprovacao.value){
        return false;
    }

    return true;

});

/*==================================================================*/
/*================= MOSTRA EDITAR ORDEM DE PAGAMENTO ===============*/
/*==================================================================*/

const mostraEditarOrdemDePagamento = computed(() => {

    if(dado?.value?.alterado === true){
        return true;
    }

    return false;

});

/*==================================================================*/
/*================== ALTERA DADO ===================================*/
/*==================================================================*/

const alteraDado = (update = {}) => {

    /*==================================================================*/
    /*====================== ALTERA DADO ===============================*/
    /*==================================================================*/

    update.alterado = true;
    let dadoUpdate = UtilObject.merge(dado.value, update);

    /*==================================================================*/
    /*====================== ATUALIZA PAGAMENTO ========================*/
    /*==================================================================*/

    let pagamentoUpdate = pagamento.value;
    pagamentoUpdate.valor = dadoUpdate?.valorTotal ?? 0,
    pagamentoUpdate.dataDoPagamento = dadoUpdate?.dataPrevisaoPagamento ?? '',

    dadoUpdate.parcelas = [pagamentoUpdate];

    /*==================================================================*/
    /*======================= SALTA ALTERAÇÃO ==========================*/
    /*==================================================================*/

    dado.value = dadoUpdate;

};

/*==================================================================*/
/*================== RECALCULA VALOR TOTAL DAS DESPESAS ============*/
/*==================================================================*/

const recalculaValorTotalDespesas = (despesas = []) => {

    let valorTotal = despesas?.reduce((acc, despesa = {}) => {

        if(despesa?.remover === true){
            return acc;
        }

        return acc + UtilMoney.toNumber(despesa?.valor);

    }, 0);

    return valorTotal;

};

/*==================================================================*/
/*================== ADICIONA DESPESA ==============================*/
/*==================================================================*/

const adicionaDespesa = () => {

    let despesas = dado?.value?.despesas ?? [];

    despesas.push({
        id: UtilID.generateDateTimeID(),
        numero: '---',
        rascunho: true,
        remover: false,
        despesa: '',
        dataDaDespesa: '',
        dataCriacao: UtilDateTime.todayUTC(),
        valor: 0,
        veicular: false,
        veiculoTipo: "",
        veiculoPlaca: "",
        veiculoKMInicial: 0,
        veiculoKMFinal: 0,
        observacao: '',
        anexos: [],
        
    });

    alteraDado({
        despesas: despesas,
    });

};

/*==================================================================*/
/*================== ALTERA DESPESA ================================*/
/*==================================================================*/

const alteraDespesa = (update = {}) => {

    let despesas = dado?.value?.despesas?.map((despesa = {}) => {

        if(despesa?.id != update?.id){
            return despesa;
        }

        return UtilObject.merge(despesa, update);

    }) ?? [];

    let valorTotal = recalculaValorTotalDespesas(despesas);

    alteraDado({
        despesas: despesas,
        valorTotal: valorTotal,
    });

};

/*==================================================================*/
/*================== DESCARTA DESPESA ==============================*/
/*==================================================================*/

const descartaDespesa = (update = {}) => {

    let despesas = dado?.value?.despesas?.filter((despesa = {}) => despesa?.id != update?.id) ?? [];

    let valorTotal = recalculaValorTotalDespesas(despesas);

    alteraDado({
        despesas: despesas,
        valorTotal: valorTotal,
    });

};

/*==================================================================*/
/*================== ADICIONA PARCELA ==============================*/
/*==================================================================*/

const adicionaParcela = () => {

    let parcelas = dado?.value?.parcelas ?? [];

    parcelas.push({
        id: UtilID.generateDateTimeID(),
        numero: '---',
        rascunho: true,
        remover: false,
        descricao: '',
        dataCriacao: UtilDateTime.todayUTC(),
        dataDoPagamento: '',
        valor: 0,
    });

    alteraDado({
        parcelas: parcelas,
    });

};

/*==================================================================*/
/*================== ALTERA PARCELA ================================*/
/*==================================================================*/

const alteraParcela = (update = {}) => {

    let parcelas = dado?.value?.parcelas?.map((parcela = {}) => {

        if(parcela?.id != update?.id){
            return parcela;
        }

        return UtilObject.merge(parcela, update);

    }) ?? [];

    alteraDado({
        parcelas: parcelas,
    });

};

/*==================================================================*/
/*================== DESCARTA PARCELA ==============================*/
/*==================================================================*/

const descartaParcela = (update = {}) => {

    let parcelas = dado?.value?.parcelas?.filter((parcela = {}) => parcela?.id != update?.id) ?? [];

    alteraDado({
        parcelas: parcelas,
    });

};

/*==================================================================*/
/*================== PREPARA APROVADORES ===========================*/
/*==================================================================*/

const preparaAprovadores = (aprovadores = []) => {

    return aprovadores.map((aprovador = {}) => ({

        id: aprovador.id ?? '',
        codigo: aprovador.codigo ?? '',
        status: aprovador.status ?? 'Valido',
        aprovadorTipo: aprovador.aprovadorTipo ?? 'Nenhum',
        aprovador: aprovador.aprovador ?? {},
        decisao: aprovador.decisao ?? 'Nula',
        justificativa: aprovador.justificativa ?? '',
        dataDecisao: aprovador.dataDecisao ?? '',

    }));

};

/*==================================================================*/
/*================== PREPARA DESPESAS ==============================*/
/*==================================================================*/

const preparaDespesas = async (despesas = []) => {

    let despesasAUX = [];

    for(const despesa of despesas ?? []){

        let despesaAUX = {
            id: despesa?.id || '',
            numero: despesa?.numero || '',
            rascunho: false,
            remover: false,
            despesa: despesa?.despesa || {},
            dataCriacao: despesa?.dataCriacao || '',
            dataDaDespesa: despesa?.dataDaDespesa || '',
            valor: despesa?.valor || 0,
            veicular: despesa?.veicular || false,
            veiculoTipo: despesa?.veiculoTipo || '',
            veiculoPlaca: despesa?.veiculoPlaca || '',
            veiculoKMInicial: despesa?.veiculoKMInicial || 0,
            veiculoKMFinal: despesa?.veiculoKMFinal || 0,
            observacao: despesa?.observacao ?? '',
            anexos: await UtilURL.manyToFile(despesa?.anexos ?? []),
        }

        despesasAUX.push(despesaAUX);

    }

    return despesasAUX

};

/*==================================================================*/
/*================== PREPARA PARCELAS ==============================*/
/*==================================================================*/

const preparaParcelas = (parcelas = []) => {

    return parcelas.map((parcela = {}) => ({
        id: parcela.id ?? '',
        numero: parcela.numero || '',
        rascunho: false,
        remover: false,
        descricao: parcela.descricao ?? '',
        dataCriacao: parcela.dataCriacao ?? '',
        dataDoPagamento: parcela.dataDoPagamento ?? '',
        valor: parcela.valor ?? 0,

    }));

};

/*==================================================================*/
/*================== PREPARA DADOS =================================*/
/*==================================================================*/

const preparaDados = async (dadoParaPreparar = {}) => {

    return {
        alterado: false,
        id: dadoParaPreparar.id ?? '',
        numero: dadoParaPreparar.numero ?? '',
        status: dadoParaPreparar.status ?? '',
        emitente: dadoParaPreparar.emitente ?? {},
        requisitante: dadoParaPreparar.requisitante ?? {},
        tipo: dadoParaPreparar.tipo ?? '',
        notaFiscal: dadoParaPreparar.notaFiscal ?? '',
        tipoCredor: dadoParaPreparar.tipoCredor ?? '',
        credorColaborador: dadoParaPreparar.credorColaborador ?? {},
        credorFornecedor: dadoParaPreparar.credorFornecedor ?? {},
        formaDePagamento: dadoParaPreparar.formaDePagamento ?? '',
        recorrencia: dadoParaPreparar.recorrencia ?? false,
        recorrenciaQuantidade: dadoParaPreparar.recorrenciaQuantidade ?? 0,
        dataCriacao: dadoParaPreparar.dataCriacao ?? '',
        dataPrevisaoPagamento: dadoParaPreparar.dataPrevisaoPagamento ?? '',
        dataAprovacaoPagamento: dadoParaPreparar.dataAprovacaoPagamento ?? '',
        valorTotal: dadoParaPreparar.valorTotal ?? 0,
        justificativa: dadoParaPreparar.justificativa ?? '',
        job: dadoParaPreparar.job ?? {},
        ordemDeServico: dadoParaPreparar.ordemDeServico ?? {},
        owners: dadoParaPreparar.owners ?? [],
        despesas: await preparaDespesas(dadoParaPreparar.despesas ?? []),
        parcelas: preparaParcelas(dadoParaPreparar.parcelas ?? []),
        aprovadores: preparaAprovadores(dadoParaPreparar.aprovadores ?? []),
    };

};

/*==================================================================*/
/*================= EDITAR ORDEM DE PAGAMENTO ======================*/
/*==================================================================*/

const editarOrdemDePagamento = async () => {

    let despesas = [];

    for(const despesa of dado?.value?.despesas ?? []){

        let despesaAUX = {
            id: despesa.id || "",
            numero: despesa.numero || '',
            rascunho: despesa.rascunho || false,
            remover: despesa.remover || false,
            despesa: despesa?.despesa?.id || '',
            dataCriacao: despesa?.dataCriacao || '',
            dataDaDespesa: despesa?.dataDaDespesa || '',
            valor: despesa?.valor || 0,
            veicular: despesa?.veicular || false,
            veiculoTipo: despesa?.veiculoTipo || '',
            veiculoPlaca: despesa?.veiculoPlaca || '',
            veiculoKMInicial: despesa?.veiculoKMInicial || 0,
            veiculoKMFinal: despesa?.veiculoKMFinal || 0,
            observacao: despesa?.observacao ?? '',
            anexos: await UtilFile.manyToBase64(despesa?.anexos ?? []),
        }

        despesas.push(despesaAUX);

    }

    const parcelas = dado.value?.parcelas?.map(parcela => ({
        id: parcela.id || "",
        numero: parcela.numero || '',
        rascunho: parcela.rascunho || false,
        remover: parcela.remover || false,
        descricao: parcela?.descricao || '',
        dataCriacao: parcela?.dataCriacao || '',
        dataDoPagamento: parcela?.dataDoPagamento || '',
        valor: parcela?.valor || 0,
    }));

    const ordemDePagamentoValores = {
        id: dado.value.id || "",
        tipo: dado.value?.tipo || "",
        emitente: dado.value?.emitente?.id || "",
        requisitante: dado.value?.requisitante?.id || "",
        notaFiscal: dado.value?.notaFiscal || "",
        tipoCredor: dado.value?.tipoCredor || "",
        credorColaborador: dado.value?.credorColaborador?.id || "",
        credorFornecedor: dado.value?.credorFornecedor?.id || "",
        formaDePagamento: dado.value?.formaDePagamento || "",
        justificativa: dado.value?.justificativa || "",
        valorTotal: dado.value?.valorTotal || 0,
        recorrencia: dado.value?.recorrencia || false,
        recorrenciaQuantidade: dado.value?.recorrenciaQuantidade || 0,
        dataCriacao: dado.value?.dataCriacao || "",
        dataPrevisaoPagamento: dado.value?.dataPrevisaoPagamento || "",
        job: dado.value?.job?.id || "",
        ordemDeServico: dado.value?.ordemDeServico?.id || "",
        despesas: despesas || [],
        parcelas: parcelas || [],
    };

    // Chama o service para Editar a ordem de pagamento
    let response = await OrdemDePagamento.editarOrdemDePagamento(ordemDePagamentoValores);
    if(response?.status != 200) {
        throw new Error("Falha ao editar Ordem de Pagamento");
    }

    // Fechar modal após sucesso
    await fecharModal();

};

/*==================================================================*/
/*================== ENVIAR PARA APROVAÇÃO =========================*/
/*==================================================================*/

const enviarParaAprovacao = async () => {

    const ordemDePagamentoValores = {
        id: dado.value?.id || "",
    };

    // Chama o service para enviar a ordem de pagamento para aprovação
    let response = await OrdemDePagamento.enviarOrdemDePagamentoParaAprovacao(ordemDePagamentoValores);
    if(response?.status != 200) {
        throw new Error("Falha ao enviar Ordem de Pagamento para Aprovação");
    }

    // Fechar modal após sucesso
    await fecharModal();


};

/*==================================================================*/
/*================== ABRIR MODAL ===================================*/
/*==================================================================*/

const abrirModal = async (dadoParaEditar = {}) => {

    dado.value = await preparaDados(dadoParaEditar);
    await modalFullscreenRef.value?.abrirModal();

};

/*==================================================================*/
/*================== FECHAR MODAL ==================================*/
/*==================================================================*/

const fecharModal = async () => {
    await modalFullscreenRef.value?.fecharModal();
};

/*==================================================================*/
/*================== AO FECHAR MODAL ===============================*/
/*==================================================================*/

const aoFecharModal = async () => {

    await props.onClose?.();

};

/*==================================================================*/
/*================== EXPOSE ========================================*/
/*==================================================================*/

defineExpose({ abrirModal, fecharModal });

</script>

<template>

    <ModalFullscreen
        ref="modalFullscreenRef"
        :dado="dado"
        :disabled="props.disabled"
        @onClose="aoFecharModal"
    >

        <template #modal-navegacao-titulo>

            Editar Ordem de Pagamento

        </template>

        <template #modal-navegacao-botoes>

            <BotaoTextoBase
                v-if="mostraEditarOrdemDePagamento"
                :disabled="disabledEditarOrdemDePagamento"
                :click="editarOrdemDePagamento"
            >
                Editar Ordem de Pagamento
            </BotaoTextoBase>

            <BotaoTextoBase
                v-if="mostraEnviarParaAprovacao"
                color="#4CAF50"
                :disabled="disabledEnviarParaAprovacao"
                :click="enviarParaAprovacao"
            >
                Enviar para Aprovação
            </BotaoTextoBase>

        </template>

        <template #modal-conteudo>

            <ConteudoModal
                ref="conteudoModalRef"
                :disabled="props.disabled"
                :dado="dado"
                :pagamento="pagamento"
                :ordemDePagamentoRequisitantes="props.ordemDePagamentoRequisitantes"
                :ordemDePagamentoCredorColaboradores="props.ordemDePagamentoCredorColaboradores"
                :ordemDePagamentoCredorFornecedores="props.ordemDePagamentoCredorFornecedores"
                :ordemDePagamentoDespesas="props.ordemDePagamentoDespesas"
                :ordemDePagamentoJobs="props.ordemDePagamentoJobs"
                @update:alteraDado="alteraDado"
                @update:adicionaDespesa="adicionaDespesa"
                @update:alteraDespesa="alteraDespesa"
                @update:descartaDespesa="descartaDespesa"
                @update:adicionaParcela="adicionaParcela"
                @update:alteraParcela="alteraParcela"
                @update:descartaParcela="descartaParcela"
            />

        </template>

    </ModalFullscreen>
    
</template>

<style scoped>
</style>
